import { useEffect, useState } from "react";
import { SessionNotes } from "@/components/clinician/SessionNotes";
import { CaseloadDigestCard } from "@/components/clinician/CaseloadDigestCard";
import { StageHeadsupSetting } from "@/components/clinician/StageHeadsupSetting";
import { ClinicianRewardProfile } from "@/components/client/RewardProfile";
import { NudgeHistory } from "@/components/clinician/NudgeHistory";
import { RewardIdeas } from "@/components/clinician/RewardIdeas";
import { KindnessCard } from "@/components/clinician/KindnessCard";
import { ChallengeCreator } from "@/components/clinician/ChallengeCreator";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { WelcomeTour } from "@/components/WelcomeTour";
import { ModerationQueue } from "@/components/ModerationQueue";
import { ReminderInsights } from "@/components/ReminderInsights";
import { MonthlyReport } from "@/components/MonthlyReport";
import { ClientAlerts } from "@/components/ClientAlerts";
import { PatternInsights } from "@/components/PatternInsights";
import { InviteCodeButtons, AlertNotifications, ClientList } from "@/components/clinician/ClientList";
import { MilestonesPanel } from "@/components/clinician/MilestonesPanel";
import { ClientHeader, ClientWeekRecap, AchievementsAndReflections, ChartsRow, WellbeingRow, HabitsCard } from "@/components/clinician/ClientDetailParts";
import { RecommendationForm } from "@/components/clinician/RecommendationForm";
import { KeystoneStrip } from "@/components/clinician/KeystoneStrip";
import { AiReviewInbox } from "@/components/clinician/AiReviewInbox";
import { ClinicianTabs, DetailTabs } from "@/components/clinician/ClinicianTabs";
import { ExploreTool } from "@/components/clinician/ExploreTool";
import { HabitGrid } from "@/components/client/HabitGrid";

export default function ClinicianDashboard() {
  const [data, setData] = useState({ clients: [], clinician_code: "", notifications: [] });
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState(() => sessionStorage.getItem("hc_ctab") || "clients");
  const [sub, setSub] = useState("habits");
  useEffect(() => { sessionStorage.setItem("hc_ctab", tab); }, [tab]);
  const [detail, setDetail] = useState(null);
  const [articles, setArticles] = useState([]);
  const [exploreParams, setExploreParams] = useState({ metric: "mood", habit_id: null });
  const [includeNotes, setIncludeNotes] = useState(false);

  const loadList = async () => {
    const [r, l] = await Promise.all([api.get("/clinician/clients"), api.get("/learn")]);
    setData(r.data);
    setArticles(l.data.articles);
    if (r.data.clients.length > 0) setSelected((cur) => cur || r.data.clients[0].id);
  };

  const loadDetail = async (id) => {
    const r = await api.get(`/clinician/client/${id}`);
    setDetail(r.data);
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => { if (selected) loadDetail(selected); }, [selected]);

  const dismissNotifications = async () => {
    await api.post("/clinician/notifications/read");
    setData((d) => ({ ...d, notifications: [] }));
  };

  const unlinkClient = async () => {
    if (!window.confirm(`Remove ${detail.client.name} from your member list? They keep their account and history; your seat count adjusts automatically.`)) return;
    await api.post(`/clinician/client/${selected}/unlink`);
    toast(`${detail.client.name} removed from your list.`);
    setSelected(null);
    setDetail(null);
    const r = await api.get("/clinician/clients");
    setData(r.data);
    if (r.data.clients.length > 0) setSelected(r.data.clients[0].id);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(data.clinician_code || "");
    toast.success("Code copied. Share it with your member.");
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/beta?code=${data.clinician_code}`);
    toast.success("Invite link copied — it opens the beta welcome page with your code filled in.");
  };

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printSummary = async () => {
    try {
      const r = await api.get(`/clinician/client/${selected}/summary.pdf`, { responseType: "blob", params: { metric: exploreParams.metric, habit_id: exploreParams.habit_id || undefined, weeks: 26, include_notes: includeNotes } });
      downloadBlob(r.data, `root-and-rose-${detail.client.name.replace(/\s+/g, "_")}-summary.pdf`);
      toast.success(includeNotes ? "Summary downloaded — page 2 holds your private notes." : "One-page summary downloaded.");
    } catch { toast.error("Couldn't build the PDF right now."); }
  };

  const exportCsv = async () => {
    const r = await api.get(`/clinician/client/${selected}/export.csv`, { responseType: "blob" });
    downloadBlob(r.data, `root-and-rose-${detail.client.name.replace(/\s+/g, "_")}.csv`);
    toast.success("CSV downloaded.");
  };

  const assignTracker = async (body) => {
    await api.post(`/clinician/client/${selected}/trackers`, body);
    toast.success(`"${body.name}" assigned — it appears on their next check-in.`);
    loadDetail(selected);
  };

  const setFocus = async (habit, focus) => {
    await api.patch(`/clinician/client/${selected}/habits/${habit.id}/focus`, { focus });
    toast.success(`"${habit.title}" set to ${focus} for ${detail.client.name}.`);
    loadDetail(selected);
  };

  const archiveTracker = async (id) => {
    await api.delete(`/clinician/trackers/${id}`);
    toast("Tracker archived. Past entries are kept.");
    loadDetail(selected);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 relative z-10">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-amber-800">Coach view</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-stone-900 mt-1">People I support, in one calm place.</h1><p className="text-xs text-stone-500 mt-1" data-testid="coach-disclaimer">Peer support, not therapy or medical care. You see only what each person chooses to share.</p>
        </div>
        <InviteCodeButtons code={data.clinician_code} onCopyCode={copyCode} onCopyLink={copyInviteLink} />
      </div>

      <WelcomeTour role="clinician" />
      <AlertNotifications notifications={data.notifications} onSelect={setSelected} onDismiss={dismissNotifications} />
      <ClinicianTabs value={tab} onChange={setTab} reviewCount={(data.ai_review?.pending || 0) + (data.ai_review?.held || 0)} />

      {tab === "review" && <div data-testid="ctab-panel-review"><AiReviewInbox onChanged={() => { loadList(); if (selected) loadDetail(selected); }} /></div>}

      {tab === "community" && <div data-testid="ctab-panel-community"><ChallengeCreator /></div>}

      {tab === "insights" && (
        <div className="space-y-6" data-testid="ctab-panel-insights">
          {!selected && <div className="warm-card p-10 text-center text-stone-500">Pick a member in the People I support tab first.</div>}
          {selected && detail && (
            <>
              <div className="text-sm text-stone-500">Exploring <strong className="text-stone-800">{detail.client.name}</strong></div>
              <ExploreTool clientId={selected} onParams={setExploreParams} />
              <PatternInsights clientId={selected} forClinician />
              <MonthlyReport clientId={selected} />
            </>
          )}
          <CaseloadDigestCard />
          <StageHeadsupSetting onChanged={() => { loadList(); if (selected) loadDetail(selected); }} />
        </div>
      )}

      {tab === "clients" && (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 min-w-0" data-testid="ctab-panel-clients">
          <ClientList clients={data.clients} selected={selected} onSelect={setSelected} onNudged={loadList} />

          <div className="xl:col-span-9 space-y-6 min-w-0">
            {!detail && <div className="warm-card p-10 text-center text-stone-500">Select a member to view their journey.</div>}
            {detail && (
              <>
                <ClientAlerts alerts={detail.alerts} struggle={detail.struggle} />
                <ClientWeekRecap recap={detail.week_recap} />
                <ClientHeader detail={detail} onExport={exportCsv} onUnlink={unlinkClient} onPdf={printSummary} includeNotes={includeNotes} onIncludeNotes={setIncludeNotes} />
                <KeystoneStrip habits={detail.habits} />
                <SessionNotes clientId={selected} />
                <DetailTabs value={sub} onChange={setSub} />

                {sub === "habits" && (
                  <div className="space-y-6" data-testid="dtab-panel-habits">
                    <HabitGrid endpoint={`/clinician/client/${selected}/grid`} readOnly title="Habit patterns" />
                    <HabitsCard habits={detail.habits} stageRequests={detail.stage_requests} stageHeadsup={detail.stage_headsup} onFocus={setFocus} clientId={selected} onChanged={() => { loadDetail(selected); loadList(); }} />
                    <ReminderInsights clientId={selected} habits={detail.habits} onSent={() => loadDetail(selected)} />
                  </div>
                )}
                {sub === "wellbeing" && (
                  <div className="space-y-6" data-testid="dtab-panel-wellbeing">
                    <ChartsRow detail={detail} />
                    <ClinicianRewardProfile clientId={selected} />
                    <RewardIdeas clientId={selected} />
                    <WellbeingRow detail={detail} onAssign={assignTracker} onArchive={archiveTracker} />
                    <KindnessCard kindness={detail.kindness} />
                    <MilestonesPanel clientId={selected} data={detail.milestones} onChanged={() => loadDetail(selected)} />
                    <AchievementsAndReflections detail={detail} />
                    <button onClick={() => setTab("insights")} data-testid="goto-insights" className="text-sm font-semibold text-amber-800 hover:text-amber-900">Open the symptom × habit explorer →</button>
                  </div>
                )}
                {sub === "notes" && (
                  <div className="space-y-6" data-testid="dtab-panel-notes">
                    <NudgeHistory clientId={selected} />
                    <RecommendationForm clientId={selected} articles={articles} recent={detail.recommendations} onSent={() => loadDetail(selected)} />
                    <ModerationQueue />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
